
import Vue from 'vue';
import { useStore as useShapeStore } from '@/stores/shape';
import { useStore as useShapeStoreV2 } from '@/stores/v2/shape';
import { useStore as useProductStoreV2 } from '@/stores/v2/product';
import { useStore as useProductStore } from '@/stores/product';
import {
  CvGrid,
  CvRow,
  CvColumn,
  CvButton,
  CvButtonSet,
  CvCheckbox,
  CvIconButton,
} from '@carbon/vue';
import Close16 from '@carbon/icons-vue/es/close/16';
import CheckmarkFilled16 from '@carbon/icons-vue/es/checkmark--filled/16';
import CloseFilled16 from '@carbon/icons-vue/es/close--filled/16';
import WarningFilled16 from '@carbon/icons-vue/es/warning--filled/16';
import ChevronLeft32 from '@carbon/icons-vue/es/chevron--left/32';
import ChevronRight32 from '@carbon/icons-vue/es/chevron--right/32';
import ZoomArea24 from '@carbon/icons-vue/es/zoom--area/24';
import AddAlt24 from '@carbon/icons-vue/es/add--alt/24';
import SubtractAlt24 from '@carbon/icons-vue/es/subtract--alt/24';
import { AnnotationShape } from '@/web-components/import-wizard/typings';
import {
  removeMagnify,
  magnify,
} from '@/components/SmallDetails/utils/magnify';

export default Vue.extend({
  name: 'SmallDetailsMultipleLogos',
  components: {
    CvGrid,
    CvColumn,
    CvRow,
    Close16,
    CheckmarkFilled16,
    WarningFilled16,
    CloseFilled16,
    CvButton,
    CvButtonSet,
    CvCheckbox,
    ChevronRight32,
    ChevronLeft32,
    ZoomArea24,
    AddAlt24,
    CvIconButton,
    SubtractAlt24,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
    useVersion2: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      activeIdx: 1,
      isZoomEnabled: false,
      zoomSize: 2,
    };
  },
  setup() {
    const { shapes, removeShape } = useShapeStore();

    return {
      shapes,
      removeShape,
    };
  },
  computed: {
    currentShape(): AnnotationShape {
      return this.relevantShapes[this.activeIdx - 1];
    },
    relevantShapes(): AnnotationShape[] {
      const store = this.useVersion2 ? useShapeStoreV2() : useShapeStore();

      return store.shapes.filter((x) => x.smallDetailsResult !== undefined);
    },
    length(): number {
      return this.relevantShapes.length;
    },
    notConfirmed(): number {
      const store = this.useVersion2 ? useShapeStoreV2() : useShapeStore();

      return store.shapes.filter(
        (x) =>
          x.smallDetailsResult &&
          x.smallDetailsResult.status !== 'Good' &&
          x.smallDetailsResult.status !== 'NoCheck' &&
          x.smallDetailsResult.approvedAt === undefined
      ).length;
    },
    imageUrl(): string | undefined {
      if (this.currentShape.kind === 'Text') {
        return this.currentShape.smallDetailsResult &&
          this.currentShape.smallDetailsResult.status !== 'NoCheck'
          ? URL.createObjectURL(
              this.currentShape.smallDetailsResult.pngFileBlob
            )
          : undefined;
      }

      if (this.currentShape.kind === 'Logo') {
        return this.currentShape.smallDetailsResult &&
          this.currentShape.smallDetailsResult.status !== 'NoCheck'
          ? URL.createObjectURL(
              this.currentShape.smallDetailsResult.pngFileBlob
            )
          : URL.createObjectURL(this.currentShape.pngFile as Blob);
      }
      return undefined;
    },
  },
  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.$modal.show('small-details-multiple-logos-modal');
      } else {
        this.$modal.hide('small-details-multiple-logos-modal');
      }
    },
    activeIdx() {
      // reset magnify glass
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      let element = undefined;
      let tagName = 'w2p-import-wizard-wrapper';

      if (subtarget && subtarget.shadowRoot) {
        element = subtarget.shadowRoot.getElementById('magnifier-glass');
      } else {
        const oldVersionTarget = document.querySelector('w2p-import-wizard');
        tagName = 'w2p-import-wizard';
        element =
          oldVersionTarget && oldVersionTarget.shadowRoot
            ? oldVersionTarget.shadowRoot.getElementById('magnifier-glass')
            : document.getElementById('magnifier-glass');
      }

      // TODO: in 2.18 version revert to old changes
      // const element =
      //   subtarget && subtarget.shadowRoot
      //     ? subtarget.shadowRoot.getElementById('magnifier-glass')
      //     : document.getElementById('magnifier-glass');

      element && element.remove();
      magnify('myimage', this.zoomSize, tagName);
    },
    zoomSize() {
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      let tagName;

      if (subtarget && subtarget.shadowRoot) {
        tagName = 'w2p-import-wizard-wrapper';
      } else {
        tagName = 'w2p-import-wizard';
      }
      removeMagnify(tagName);
      magnify('myimage', this.zoomSize, tagName);
    },
  },
  mounted() {
    if (this.isOpen) {
      this.$modal.show('small-details-multiple-logos-modal');
    }
  },

  methods: {
    move(amount: number) {
      if (amount === 1 && this.length === 1) {
        this.$emit('action:back-to-wizard');
        return;
      }
      let newActive;
      const newIndex = this.activeIdx + amount;
      if (newIndex > this.length) newActive = 1;
      if (newIndex === 0) newActive = this.length;

      this.activeIdx = newActive || newIndex;
    },
    onAcceptLogo(checked: boolean) {
      if (this.currentShape.smallDetailsResult) {
        const value = checked ? new Date().toISOString() : undefined;
        this.currentShape.smallDetailsResult.approvedAt = value;
      }
    },
    onUploadNewLogo() {
      this.$emit('action:back-to-wizard');
      if (this.useVersion2) {
        var productStore = useProductStoreV2();
        var shapeStore = useShapeStoreV2();
        const positionId = shapeStore.positionShapeMap.find(
          (x) => x.logoId === this.currentShape.id
        )?.id;
        productStore.setSelectedPrintPosition(positionId as string);
      }
      this.removeShape(this.currentShape.id);
      this.$emit('action:open-logo-library');
    },
    enableZoom() {
      const subtarget = document.querySelector('w2p-import-wizard-wrapper');
      let element = undefined;
      let tagName = 'w2p-import-wizard-wrapper';
      if (subtarget && subtarget.shadowRoot) {
        element = subtarget.shadowRoot.getElementById('magnifier-glass');
      } else {
        const oldVersionTarget = document.querySelector('w2p-import-wizard');
        tagName = 'w2p-import-wizard';
        element =
          oldVersionTarget && oldVersionTarget.shadowRoot
            ? oldVersionTarget.shadowRoot.getElementById('magnifier-glass')
            : document.getElementById('magnifier-glass');
      }

      // TODO: in 2.18 version revert to old changes
      // const element =
      //   subtarget && subtarget.shadowRoot
      //     ? subtarget.shadowRoot.getElementById('magnifier-glass')
      //     : document.getElementById('magnifier-glass');

      if (element) {
        this.isZoomEnabled = false;
        this.zoomSize = 2;
        removeMagnify(tagName);
      } else {
        this.isZoomEnabled = true;
        magnify('myimage', this.zoomSize, tagName);
      }
    },

    onUpdateText() {
      this.$emit('action:back-to-wizard');
      this.$modal.show('tiptap-modal', { id: this.currentShape.id });
    },
  },
});
